/**
 * Sourced from Dashboard, modified for Shop.
 * https://github.com/squareup/dashboard/blob/1e7889daf34ea472ddbd5e70a95a8f10946b8abd/frontend/dashboard/app/services/user-journey/user-journey-data-api.js#L1
 */
import { CDP } from '@squareup/cdp';
import { getEnvironment } from 'utilities/environment';
import { initTracking } from 'utilities/tracking';

const APPLICATION_NAME = 'solidshop';
const ENV_PROD = 'production';
const ENV_STAGING = 'staging';

/**
 * Service for tracking user journey data in the CDP.
 * This is a copy of https://github.com/squareup/dashboard/blob/main/frontend/dashboard/app/services/data-api.js
 * with UserJourney specific API keys.
 */
export default class UserJourneyService {
  constructor() {
    this.cdp = null;

    if (!Sq?.Tracking) {
      console.warn('Sq.Tracking not found.');
      return;
    }

    const apiKey = Sq?.Tracking?.config?.cdp_user_journeys;
    if (!apiKey) {
      console.warn('User Journey API key not found.');
      return;
    }

    // follow logic in trackAddToCart in helpers.js
    // check for analyticsTrackingEnabled since we are not tracking for advertising
    this.oneTrustConsent = !Sq.OneTrust || Sq.OneTrust.analyticsTrackingEnabled;

    if (!this.oneTrustConsent) {
      return;
    }

    const { env } = getEnvironment();
    const cdpEnvironment = env === ENV_PROD ? ENV_PROD : ENV_STAGING;

    this.cdp = new CDP({
      apiKey,
      environment: cdpEnvironment,
      useSameOrigin: env === ENV_PROD || env === ENV_STAGING,
    });

    this.identify();
  }

  /**
   * Initialize the UserJourneyService.
   * @return {Promise<UserJourneyService>}
   */
  static async init() {
    // we rely on Sq.Tracking to be available
    await initTracking();
    return new UserJourneyService();
  }

  /**
   * Identify the user in the CDP.
   */
  identify() {
    if (!this.cdp) {
      return;
    }

    this.cdp.identifyV2({
      entityId: Sq.Tracking.cdpEntityId,
      entityType: Sq.Tracking.cdpEntityType,
    });
  }

  /**
   * Track an event in the CDP.
   * @param {string} eventName
   * @param {object} eventProps
   * @returns {void}
   */
  track(eventName, eventProps = {}) {
    if (!this.cdp) {
      return;
    }

    if (!eventName) {
      throw new Error('eventName must be passed in');
    }

    this.cdp.trackV2({
      eventName,
      eventProps,
      options: {
        overrides: { context: { application: { name: APPLICATION_NAME } } },
      },
    });
  }
}
